import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import MercadoPagoConfig from 'types/models/MercadoPagoConfig';
import EditMPConfigContext from 'contexts/EditMPConfigContext';
import { getMPConfigs } from 'services/mercadoPagoServices';

export default (): JSX.Element => {
  const history = useHistory();
  const [paginationMercadoPagoConfig, setPaginationMercadoPagoConfigs] =
    useState<PaginationData<MercadoPagoConfig> | null>(null);
  const { setSelectedMPConfig } = React.useContext(EditMPConfigContext);
  const { t } = useTranslation();

  const goToPage = (url: string): void => {
    history.push(url);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = React.useCallback(async (page?: number) => {
    const paginationData = await getMPConfigs(page);
    if (paginationData) setPaginationMercadoPagoConfigs(paginationData);
  }, []);

  const renderEditButton = (mercadoPagoConfig: MercadoPagoConfig): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedMPConfig(mercadoPagoConfig);
          history.push('mercado-pago/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: MercadoPagoConfig[]): JSX.Element => {
    return (
      <>
        {paginationData.map((mercadoPagoConfig: MercadoPagoConfig) => (
          <TableRow key={mercadoPagoConfig.description}>
            <TableCell component="th" scope="row">
              {mercadoPagoConfig.description}
            </TableCell>
            <TableCell className="monospace-value-cell">{mercadoPagoConfig.token}</TableCell>
            <TableCell>{renderEditButton(mercadoPagoConfig)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="primary" onClick={() => goToPage('mercado-pago/create')}>
          {t('listMPConfigs-page-create')}
        </Button>
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listMPConfigs-page-description')}</TableCell>
              <TableCell>{t('listMPConfigs-page-token')}</TableCell>
              <TableCell>{t('listMPConfigs-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationMercadoPagoConfig?.data && paginationMercadoPagoConfig?.data.length > 0 ? (
              renderContent(paginationMercadoPagoConfig.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listMPConfigs-page-noConfigs')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationMercadoPagoConfig && (
        <TablePagination
          component="div"
          count={paginationMercadoPagoConfig.total}
          page={paginationMercadoPagoConfig.currentPage - 1}
          onPageChange={(_, page: number) => {
            loadData(page + 1);
          }}
          rowsPerPage={paginationMercadoPagoConfig.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
